import React, {
    useContext, useEffect, useMemo, useState 
} from 'react';
import {
    Box, Slide, Typography, useTheme 
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import ProgressBar from '../../components/progressBar';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';

type Props = {
    position: number
};

export const TopGoal = ({
    position
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const initialScreenWidth = useMemo(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth;
        }
        return 0;
    }, []);
    const [screenWidth, setScreenWidth] = useState(initialScreenWidth);
    const [skipAnimation, setSkipAnimation] = useState(false);
    const topGoalAnswer = useMemo(() => {
        return formContext?.answers['top_goal'] || 'health';
    }, [formContext?.answers]);
    const useStyles = useMemo(() => makeStyles(() => ({
        scrollableContainer: {
            overflow: 'scroll',
            overflowX: 'hidden',
            scrollbarWidth: 'none',
            width: '100vw',
            maxWidth: '692px',
            padding: '0 24px',
            transform: 'translate(-24px, 0)',
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pointerEvents: 'none'
        },
        title: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards 0s`
        },
        svg: {
            transform: 'translate(-30px, 0)',
            margin: '24px auto 0'
        },
        drinkLine: {
            animation: `$snake ${skipAnimation ? 0 : 1}s ease-out forwards ${skipAnimation ? 0 : 1}s`,
        },
        goalLine: {
            animation: `$snake ${skipAnimation ? 0 : 1}s ease-out forwards ${skipAnimation ? 0 : 2}s`,
        },
        drinkTextPath: {
            opacity: 1,
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s ease-out forwards ${skipAnimation ? 0 : 0.75}s`,
        },
        goalTextPath: {
            opacity: 1,
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s ease-out forwards ${skipAnimation ? 0 : 1.75}s`,
        },
        goalTextPath2: {
            opacity: 1,
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s ease-out forwards ${skipAnimation ? 0 : 1.75}s`,
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: 0 
            },
            '100%': {
                opacity: 1 
            },
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: 1 
            },
            '100%': {
                opacity: 0 
            },
        },
        '@keyframes snake': {
            '0%': {
                strokeDashoffset: `${Math.min(screenWidth * 1.467, 800)}px` 
            },
            '100%': {
                strokeDashoffset: '0px' 
            },
        },
        '@keyframes makeInteractive': {
            '0%': {
                pointerEvents: 'none' 
            },
            '100%': {
                pointerEvents: 'auto' 
            },
        }
    })), [screenWidth, skipAnimation]);
    const classes = useStyles();
    const theme = useTheme();
    const [start, setStart] = useState(false);

    const onClick = () => {
        if (!skipAnimation) setSkipAnimation(true);
        else formContext.goNext();
    };

    const GOAL_DATA = {
        health: {
            title: `OK, we'll focus on helping you <span style="color:${theme.palette.primary.main};font-weight:${TypographyWeights.BOLD};">improve your overall health and wellness.</span>`,
            upwards: true,
            label: 'Overall Health',
        },
        healthspan: {
            title: `OK, we'll focus on helping you <span style="color:${theme.palette.primary.main};font-weight:${TypographyWeights.BOLD};">slow down the effects of aging.</span>`,
            upwards: false,
            label: 'Effects of Aging',
        },
        weight: {
            title: `OK, we'll focus on helping you <span style="color:${theme.palette.primary.main};font-weight:${TypographyWeights.BOLD};">lose weight and achieve improved fitness.</span>`,
            upwards: false,
            label: 'Weight',
        },
        sleep: {
            title: `OK, we'll focus on helping you <span style="color:${theme.palette.primary.main};font-weight:${TypographyWeights.BOLD};">improve the quality of your sleep.</span>`,
            upwards: true,
            label: 'Sleep Quality',
        },
        hangover: {
            title: `OK, we'll focus on helping you <span style="color:${theme.palette.primary.main};font-weight:${TypographyWeights.BOLD};">avoid hangovers and other next-day effects.</span>`,
            upwards: false,
            label: 'Hangovers',
        },
        blackout: {
            title: `OK, we'll focus on helping you <span style="color:${theme.palette.primary.main};font-weight:${TypographyWeights.BOLD};">stop blacking (or graying) out.</span>`,
            upwards: false,
            label: 'Impact on Memory',
        },
        money: {
            title: `OK, we'll focus on helping you <span style="color:${theme.palette.primary.main};font-weight:${TypographyWeights.BOLD};">save money by spending less on alcohol.</span>`,
            upwards: true,
            label: 'Dollars Saved',
        },
        emotions: {
            title: `OK, we'll focus on helping you <span style="color:${theme.palette.primary.main};font-weight:${TypographyWeights.BOLD};">find healthier emotional coping strategies.</span>`,
            upwards: true,
            label: 'Emotional Resilience',
        },
        stress: {
            title: `OK, we'll focus on helping you <span style="color:${theme.palette.primary.main};font-weight:${TypographyWeights.BOLD};">reduce stress and anxiety.</span>`,
            upwards: false,
            label: 'Stress & Anxiety',
        },
        relationships: {
            title: `OK, we'll focus on helping you <span style="color:${theme.palette.primary.main};font-weight:${TypographyWeights.BOLD};">improve the relationships that matter most to you.</span>`,
            upwards: true,
            label: 'Relationship Quality',
        },
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const resizeListener = () => {
            setScreenWidth(window.innerWidth);
        };
        window.addEventListener('resize', resizeListener);
        setStart(true);

        setTimeout(() => {
            setSkipAnimation(true);
        }, 2500);

        return () => {
            window.removeEventListener('resize', resizeListener);
        };
    }, []);

    return (
        <CardContainer
            sectionKey={'goals'}
            goBack={formContext.goBack}
            goNext={onClick}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position}
                    sectionKey={'goals'}
                />
                <Box className={classes.scrollableContainer}>
                    <Box className={classes.content}>
                        <Slide
                            in={true}
                            direction={formContext.screenDirection}
                            timeout={500}
                            mountOnEnter
                            unmountOnExit
                        >
                            <Typography
                                variant={TypographyVariants.H3}
                                className={classes.title}
                                dangerouslySetInnerHTML={{
                                    __html: GOAL_DATA[topGoalAnswer].title 
                                }}
                            />
                        </Slide>
                        {start && (
                            <svg
                                className={classes.svg}
                                style={{
                                    width: `${Math.min(screenWidth + 30, 698)}px`,
                                    height: GOAL_DATA[topGoalAnswer].upwards ? '298px' : '390px'
                                }}
                            >
                                {GOAL_DATA[topGoalAnswer].upwards ? (
                                    <g>
                                        <text
                                            style={{
                                                transform: 'translate(0, 6px)' 
                                            }}
                                        >
                                            <textPath
                                                startOffset={40}
                                                href="#goalPath"
                                            >
                                                {GOAL_DATA[topGoalAnswer].label}
                                            </textPath>
                                        </text>
                                        <path
                                            id={'goalPath'}
                                            className={classes.goalTextPath2}
                                            d={`M 6 265 c 0 0 ${300 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600))} 0 ${450 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600))} ${-(400 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600)))}`}
                                            fill="none"
                                            stroke={theme.palette.white}
                                            strokeWidth={'30px'}
                                        />
                                        <path
                                            className={classes.goalLine}
                                            d={`M 6 292 c 0 0 ${300 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600))} 0 ${450 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600))} ${-(400 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600)))}`}
                                            fill="none"
                                            strokeDashoffset={`${Math.min(screenWidth * 1.467, 800)}px`}
                                            strokeDasharray={`${Math.min(screenWidth * 1.467, 800)}px`}
                                            stroke={theme.palette.primary.dark}
                                            strokeLinecap={'round'}
                                            strokeWidth={'12px'}
                                        />
                                    </g>
                                ) : (
                                    <g>
                                        <text>
                                            <textPath
                                                startOffset={40}
                                                href="#goalPath"
                                            >
                                                {GOAL_DATA[topGoalAnswer].label}
                                            </textPath>
                                        </text>
                                        <path
                                            id={'goalPath'}
                                            className={classes.goalTextPath}
                                            d={`M 6 128 c 0 0 ${300 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600))} 0 ${450 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600))} ${400 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600))}`}
                                            fill="none"
                                            stroke={theme.palette.white}
                                            strokeWidth={'30px'}
                                        />
                                        <path
                                            className={classes.goalLine}
                                            d={`M 6 100 c 0 0 ${300 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600))} 0 ${450 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600))} ${400 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600))}`}
                                            fill="none"
                                            strokeDashoffset={`${Math.min(screenWidth * 1.467, 800)}px`}
                                            strokeDasharray={`${Math.min(screenWidth * 1.467, 800)}px`}
                                            stroke={theme.palette.primary.dark}
                                            strokeLinecap={'round'}
                                            strokeWidth={'12px'}
                                        />
                                    </g>
                                )}
                                <path
                                    className={classes.drinkLine}
                                    d={`M 6 12 c ${GOAL_DATA[topGoalAnswer].upwards ? '0' : '18'} 0 ${300 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600)) + (GOAL_DATA[topGoalAnswer].upwards ? 0 : 18)} 0 ${450 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600)) + (GOAL_DATA[topGoalAnswer].upwards ? 0 : 18)} ${400 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600))}`}
                                    fill="none"
                                    strokeDashoffset={`${Math.min(screenWidth * 1.467, 800)}px`}
                                    strokeDasharray={`${Math.min(screenWidth * 1.467, 800)}px`}
                                    stroke={theme.palette.white}
                                    strokeLinecap={'round'}
                                    strokeWidth={'30px'}
                                />
                                <path
                                    className={classes.drinkLine}
                                    d={`M 6 12 c ${GOAL_DATA[topGoalAnswer].upwards ? '0' : '18'} 0 ${300 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600)) + (GOAL_DATA[topGoalAnswer].upwards ? 0 : 18)} 0 ${450 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600)) + (GOAL_DATA[topGoalAnswer].upwards ? 0 : 18)} ${400 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600))}`}
                                    fill="none"
                                    strokeDashoffset={`${Math.min(screenWidth * 1.467, 800)}px`}
                                    strokeDasharray={`${Math.min(screenWidth * 1.467, 800)}px`}
                                    stroke={theme.palette.primary.main}
                                    strokeLinecap={'round'}
                                    strokeWidth={'12px'}
                                />
                                <text>
                                    <textPath
                                        startOffset={40}
                                        href="#drinkPath"
                                    >
                                        Total Drinks
                                    </textPath>
                                </text>
                                <path
                                    id={'drinkPath'}
                                    className={classes.drinkTextPath}
                                    d={`M 6 40 c ${GOAL_DATA[topGoalAnswer].upwards ? '0' : '18'} 0 ${300 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600)) + (GOAL_DATA[topGoalAnswer].upwards ? 0 : 18)} 0 ${450 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600)) + (GOAL_DATA[topGoalAnswer].upwards ? 0 : 18)} ${450 + Math.max(0, Math.min((screenWidth - 375 + (317 - (692 - screenWidth)) / 317 * 58) / 375 * 600, 600))}`}
                                    fill="none"
                                    stroke={theme.palette.white}
                                    strokeWidth={'30px'}
                                />
                            </svg>
                        )}
                        <Box
                            style={{
                                height: '200px' 
                            }}
                        />
                    </Box>
                </Box>
            </Card>
        </CardContainer>
    );
};

export default TopGoal;
